import React, { createContext, useContext, useState, useEffect } from 'react';

interface UserData {
  accounts: string[];
  authToken?: string;
  account?: string | null; 
}

interface AuthContextType {
  isLoggedIn: boolean;
  userData: UserData | null;
  authToken?: string;
  login: (accounts: string[], authToken: string) => void;
  logout: () => void;
  selectAccount: (account: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

  const getLocalStorageItem = (key: string) => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(key);
    }
    return null;
  };

  const [authState, setAuthState] = useState<{ isLoggedIn: boolean; userData: UserData | null }>(() => {
    const isLoggedInStored = getLocalStorageItem('isLoggedIn') === 'true';
    const storedAccounts = getLocalStorageItem('accounts');
    const storedAuthToken = getLocalStorageItem('authToken');
    const storedAccount = getLocalStorageItem('selectedAccount'); 
  
    const userData = storedAccounts && storedAuthToken ? {
      accounts: JSON.parse(storedAccounts),
      authToken: storedAuthToken,
      account: storedAccount 
    } : null;
  
    return { isLoggedIn: isLoggedInStored, userData };
  });

  
  const selectAccount = (account: string) => {
    localStorage.setItem('selectedAccount', account); 
    setAuthState(prevState => {
        const { accounts = [], authToken = '' } = prevState.userData || {};
        
        const updatedUserData = { accounts, authToken, account };
        
        return {
            ...prevState,
            userData: updatedUserData
        };
    });
};

  const login = (accounts: string[], authToken: string) => {
    setAuthState({ isLoggedIn: true, userData: { accounts, authToken, account: null } }); 
  };

  const logout = () => {
    localStorage.removeItem('selectedAccount'); 
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('authToken');
    localStorage.removeItem('accounts');
    setAuthState({ isLoggedIn: false, userData: null });
  };

  useEffect(() => {
    if (typeof window !== 'undefined') { 
      if (authState.isLoggedIn) {
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('accounts', JSON.stringify(authState.userData?.accounts));
        localStorage.setItem('authToken', authState.userData?.authToken || '');
      } else {
        localStorage.clear();
      }
    }
  }, [authState]);

  useEffect(() => {
    if (authState.isLoggedIn) {
      localStorage.setItem('accounts', JSON.stringify(authState.userData?.accounts));
      localStorage.setItem('authToken', authState.userData?.authToken || '');
      localStorage.setItem('isLoggedIn', 'true');
    } else {
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('authToken');
      localStorage.removeItem('accounts');
    }
  }, [authState]);

  useEffect(() => {
    if (authState.isLoggedIn && authState.userData) {
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('accounts', JSON.stringify(authState.userData.accounts));
      localStorage.setItem('authToken', authState.userData.authToken || '');
    } else {
      localStorage.clear(); 
    }
  }, [authState]);

  return (
    <AuthContext.Provider value={{ ...authState, login, logout, selectAccount }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside an AuthProvider");
  }
  return context;
};
